import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Coins } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { API } from '@/App';
import BrandLogo from '@/components/BrandLogo';

export default function PartnerLogin({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const safeReadJson = async (res) => {
    try {
      // Clone before reading: Sentry fetch instrumentation may drain the original stream.
      const text = await res.clone().text();
      if (!text) return {};
      try { return JSON.parse(text); } catch { return { _raw: text }; }
    } catch { return {}; }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await safeReadJson(res);
      if (!res.ok) {
        toast.error(data.detail || 'Login failed');
      } else if (data.user?.role !== 'affiliate') {
        toast.error('This login is for affiliate partners only');
      } else {
        if (onLogin) onLogin(data.token, data.user);
        toast.success('Welcome back');
        navigate('/partner');
      }
    } catch (err) {
      toast.error('Network error');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0e0b04] flex items-center justify-center px-4 py-12 relative overflow-hidden">
      {/* Subtle gold radial backdrop */}
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(circle at center, rgba(203,161,53,0.08), transparent 60%)' }} />

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-md"
      >
        <div className="flex flex-col items-center mb-8">
          <BrandLogo />
          <div className="mt-6 flex items-center gap-2 text-[#cba135]">
            <Coins size={20} weight="duotone" />
            <span className="uppercase tracking-[0.28em] text-xs">Partner Portal</span>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-[#1a1508] border border-[#cba135]/20 rounded-lg p-8 shadow-[0_12px_48px_rgba(0,0,0,0.6)] space-y-5"
          data-testid="partner-login-form"
        >
          <div>
            <h1 className="heading-font text-2xl font-medium text-[#cba135]">Partner sign in</h1>
            <p className="text-[#cba135]/60 text-sm mt-1">Access your affiliate dashboard.</p>
          </div>

          <div>
            <Label htmlFor="partner-email" className="text-[#cba135]">Email</Label>
            <Input
              id="partner-email"
              data-testid="partner-email-input"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-[#0e0b04] border-[#cba135]/20 text-[#cba135] focus:ring-[#cba135] mt-1"
              required
            />
          </div>

          <div>
            <Label htmlFor="partner-password" className="text-[#cba135]">Password</Label>
            <Input
              id="partner-password"
              data-testid="partner-password-input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-[#0e0b04] border-[#cba135]/20 text-[#cba135] focus:ring-[#cba135] mt-1"
              required
            />
          </div>

          <Button
            type="submit"
            data-testid="partner-login-submit"
            disabled={loading}
            className="w-full bg-[#cba135] hover:bg-[#b8902f] text-[#0e0b04] font-semibold disabled:opacity-50"
          >
            {loading ? 'Signing in…' : 'Sign in'}
          </Button>

          <p className="text-xs text-[#cba135]/50 text-center pt-2 border-t border-[#cba135]/10">
            Forgot your password? Contact admin — partner accounts are reset by admin only.
          </p>
        </form>
      </motion.div>
    </div>
  );
}
